/**
 * What this DEVICE remembers about audio (plan §9.5): the volume sliders, mute, and whether the player
 * has pressed "Enable audio" here before. Browser storage only — server-side persistence waits for
 * players asking — and every read and write is wrapped, because private windows and blocked site data
 * throw, and the page must work without any of it.
 */
import { DEFAULT_AUDIO_VOLUMES, isSkinHash, isUuidText, normaliseAudioVolumes, sanitiseAudioPlayerName, type AudioVolumes } from '@dimensionscore/shared';

export const AUDIO_PREFS_KEY = 'dc.audio.prefs.v1';

export interface AudioPrefs {
  readonly volumes: AudioVolumes;
  readonly muted: boolean;
  /** Pressed "Enable audio" on this device before — so a reload may try to resume without asking. */
  readonly enabledBefore: boolean;
}

export const DEFAULT_AUDIO_PREFS: AudioPrefs = { volumes: DEFAULT_AUDIO_VOLUMES, muted: false, enabledBefore: false };

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

export function readAudioPrefs(storage: StorageLike | undefined = globalThis.localStorage): AudioPrefs {
  try {
    const raw = storage?.getItem(AUDIO_PREFS_KEY);
    if (raw === null || raw === undefined) return DEFAULT_AUDIO_PREFS;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return { volumes: normaliseAudioVolumes(parsed['volumes']), muted: parsed['muted'] === true, enabledBefore: parsed['enabledBefore'] === true };
  } catch {
    return DEFAULT_AUDIO_PREFS;
  }
}

export function writeAudioPrefs(prefs: AudioPrefs, storage: StorageLike | undefined = globalThis.localStorage): void {
  try {
    storage?.setItem(AUDIO_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* the sliders still work for this page load */
  }
}

/**
 * WHETHER THIS BROWSER PROBABLY HOLDS AN AUDIO RENEWAL, and until when. The renewal itself is an
 * `httpOnly` cookie no script can see, so the page keeps this one public fact beside it: set when a
 * claim or a renewal answers, cleared on sign-out and on a refusal. It is what lets a signed-out
 * visitor's page issue NO request at all — the property every signed-out page in this SPA keeps — and
 * it is only a hint: a stale one costs one 401, a missing one costs a `/audio` in game.
 */
export const AUDIO_RENEWAL_HINT_KEY = 'dc.audio.renewal-until';

export function hasRenewalHint(nowMs: number = Date.now(), storage: StorageLike | undefined = globalThis.localStorage): boolean {
  try {
    const until = Number(storage?.getItem(AUDIO_RENEWAL_HINT_KEY) ?? '0');
    return Number.isFinite(until) && until > nowMs;
  } catch {
    return false;
  }
}

export function writeRenewalHint(untilMs: number, storage: StorageLike | undefined = globalThis.localStorage): void {
  try {
    storage?.setItem(AUDIO_RENEWAL_HINT_KEY, String(untilMs));
  } catch {
    /* the next visit asks the sidecar once, or shows the way in */
  }
}

/** Clears the renewal hint and, with it, who this device last listened as ({@link AUDIO_PLAYER_HINT_KEY}). */
export function clearRenewalHint(storage: Pick<Storage, 'removeItem'> | undefined = globalThis.localStorage): void {
  try {
    storage?.removeItem(AUDIO_RENEWAL_HINT_KEY);
    storage?.removeItem(AUDIO_PLAYER_HINT_KEY);
  } catch {
    /* nothing to undo */
  }
}

/**
 * WHO THIS DEVICE LAST LISTENED AS: the `{uuid, name, skin}` the last claim or renewal answered with.
 *
 * Stats puts the player's own name and face on its header, and the claim is what knows them — the
 * name the mod sent and the skin hash it sent beside it. The Music app has them from the companion;
 * Stats, which dials no socket of its own, reads them here when it is the first app opened in a tab.
 * Only facts the game already printed (a name, a texture hash) and only on this device; cleared with
 * the renewal hint, so a sign-out forgets them too.
 */
export const AUDIO_PLAYER_HINT_KEY = 'dc.audio.player';

export interface AudioPlayerHint {
  readonly uuid: string;
  readonly name: string;
  readonly skin: string | null;
}

export function writePlayerHint(player: AudioPlayerHint, storage: StorageLike | undefined = globalThis.localStorage): void {
  try {
    storage?.setItem(AUDIO_PLAYER_HINT_KEY, JSON.stringify({ uuid: player.uuid, name: player.name, skin: player.skin }));
  } catch {
    /* Stats falls back to the session and the directory row */
  }
}

/** The stored player, or null — for nothing stored, unreadable storage, or a value that is not one. */
export function readPlayerHint(storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage): AudioPlayerHint | null {
  try {
    const raw = JSON.parse(storage?.getItem(AUDIO_PLAYER_HINT_KEY) ?? 'null') as Record<string, unknown> | null;
    if (raw === null || typeof raw !== 'object' || !isUuidText(raw['uuid'])) return null;
    const name = sanitiseAudioPlayerName(raw['name']);
    if (name === null) return null;
    return { uuid: raw['uuid'], name, skin: isSkinHash(raw['skin']) ? raw['skin'] : null };
  } catch {
    return null;
  }
}

/** What a cue id reads as on the page. Unknown ids read as themselves — a newer mod may send more. */
export const CUE_LABELS: Readonly<Record<string, string>> = {
  mail: 'New mail',
  kit: 'Kit ready',
  tpa: 'Teleport request',
  catch: 'Caught!',
  shiny_catch: 'Shiny caught!',
  victory: 'Victory!',
  defeat: 'Defeat',
  level_up: 'Level up!',
};

export function cueLabel(id: string): string {
  return CUE_LABELS[id] ?? id.replace(/_/g, ' ');
}
